import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  AlertTitle,
  FormHelperText,
  Grid,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider
} from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import notificationService from '../../services/notificationService';
import LoadingSpinner from '../common/LoadingSpinner';
import { useTheme } from '@mui/material/styles';

/**
 * Component for configuring leak detection settings
 */
const NotificationSettings = ({ onSettingsUpdated }) => {
  const theme = useTheme();
  // Separate schedules
  const [thresholdsSchedule, setThresholdsSchedule] = useState('30 2 * * *');
  const [leakDailySchedule, setLeakDailySchedule] = useState('0 3 * * *');
  const [signalStrengthSchedule, setSignalStrengthSchedule] = useState('30 6 * * *');
  const [notificationDigestSchedule, setNotificationDigestSchedule] = useState('0 7 * * *');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningThresholds, setRunningThresholds] = useState(false);
  const [runningLeakDetection, setRunningLeakDetection] = useState(false);
  const [runningSignalStrength, setRunningSignalStrength] = useState(false);
  const [runningNotificationDigest, setRunningNotificationDigest] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [thresholdsDescription, setThresholdsDescription] = useState('Every day at 2:30 AM');
  const [leakDailyDescription, setLeakDailyDescription] = useState('Every day at 3:00 AM');
  const [signalStrengthDescription, setSignalStrengthDescription] = useState('Every day at 6:30 AM');
  const [notificationDigestDescription, setNotificationDigestDescription] = useState('Every day at 7:00 AM');
  
  // Load settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const settings = await notificationService.getLeakDetectionSettings();
        // Backward compatibility: if older server returns only 'schedule', use it for leakDaily by default
        const thr = settings.thresholdsSchedule || '30 2 * * *';
        const leak = settings.leakDailySchedule || settings.schedule || '0 3 * * *';
        const signal = settings.signalStrengthSchedule || '30 6 * * *';
        const digest = settings.notificationDigestSchedule || '0 7 * * *';

        setThresholdsSchedule(thr);
        setLeakDailySchedule(leak);
        setSignalStrengthSchedule(signal);
        setNotificationDigestSchedule(digest);

        // Generate descriptions
        setThresholdsDescription(parseCronDescription(thr));
        setLeakDailyDescription(parseCronDescription(leak));
        setSignalStrengthDescription(parseCronDescription(signal));
        setNotificationDigestDescription(parseCronDescription(digest));
      } catch (err) {
        setError('Failed to load settings. Please try again later.');
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    }
    
    fetchSettings();
  }, []);
  
  // Create a friendly description for a cron expression
  function parseCronDescription(cronExpression) {
    // Simple cron expression parser for user-friendly descriptions
    try {
      const parts = cronExpression.split(' ');
      if (parts.length !== 5) throw new Error('Invalid cron expression');
      
      const minute = parts[0];
      const hour = parts[1];
      const dayOfMonth = parts[2];
      const month = parts[3];
      const dayOfWeek = parts[4];
      
      let description = '';

      // Daily at specific hour:minute (e.g., "30 2 * * *")
      if (minute !== '*' && hour !== '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        const hourNum = parseInt(hour, 10);
        const amPm = hourNum >= 12 ? 'PM' : 'AM';
        const hour12 = hourNum > 12 ? hourNum - 12 : hourNum === 0 ? 12 : hourNum;
        description = `Every day at ${hour12}:${minute.padStart(2, '0')} ${amPm}`;
      } else if (minute === '0' && hour !== '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        // Every day at specific hour (on the hour)
        const hourNum = parseInt(hour, 10);
        const amPm = hourNum >= 12 ? 'PM' : 'AM';
        const hour12 = hourNum > 12 ? hourNum - 12 : hourNum === 0 ? 12 : hourNum;
        description = `Every day at ${hour12}:00 ${amPm}`;
      } else if (minute !== '*' && hour !== '*' && dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
        // Specific days of week at specific time
        const daysOfWeek = {
          '0': 'Sunday',
          '1': 'Monday',
          '2': 'Tuesday',
          '3': 'Wednesday',
          '4': 'Thursday',
          '5': 'Friday',
          '6': 'Saturday'
        };
        
        let days = '';
        if (dayOfWeek.includes(',')) {
          const daysList = dayOfWeek.split(',');
          days = daysList.map(d => daysOfWeek[d]).join(', ');
        } else if (dayOfWeek.includes('-')) {
          const [start, end] = dayOfWeek.split('-');
          days = `${daysOfWeek[start]} through ${daysOfWeek[end]}`;
        } else {
          days = daysOfWeek[dayOfWeek];
        }
        
        const hourNum = parseInt(hour, 10);
        const amPm = hourNum >= 12 ? 'PM' : 'AM';
        const hour12 = hourNum > 12 ? hourNum - 12 : hourNum === 0 ? 12 : hourNum;
        description = `Every ${days} at ${hour12}:${minute.padStart(2, '0')} ${amPm}`;
      } else {
        description = 'Custom schedule';
      }
      return description;
    } catch (err) {
      console.error('Error parsing cron expression:', err);
      return 'Custom schedule';
    }
  };

  const runJob = async (jobName, fn, setRunning) => {
    try {
      setRunning(true);
      setError(null);
      const result = await fn();
      const created = typeof result?.notificationsCreated === 'number' ? result.notificationsCreated : null;
      setSuccessMessage(created === null ? `${jobName} completed successfully` : `${jobName} completed successfully (created ${created} notifications)`);
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      setError(`Failed to run ${jobName}. Please try again later.`);
      console.error(`Error running ${jobName}:`, err);
    } finally {
      setRunning(false);
    }
  };

  const handleRunThresholds = () => runJob('Thresholds', () => notificationService.runDailyThresholds(), setRunningThresholds);
  const handleRunLeakDetection = () => runJob('Leak Detection', () => notificationService.runLeakDetection(), setRunningLeakDetection);
  const handleRunSignalStrength = () => runJob('Signal Strength', () => notificationService.runSignalStrengthCheck(), setRunningSignalStrength);
  const handleRunNotificationDigest = () => runJob('Email Digest', () => notificationService.runNotificationDigest(), setRunningNotificationDigest);
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      // Update all schedules
      await notificationService.updateDailyThresholdsSchedule(thresholdsSchedule);
      await notificationService.updateLeakDailySchedule(leakDailySchedule);
      await notificationService.updateSignalStrengthSchedule(signalStrengthSchedule);
      await notificationService.updateNotificationDigestSchedule(notificationDigestSchedule);
      
      // In a real implementation, we would also update the other settings
      // This would require adding those endpoints to the backend
      
      setSuccessMessage('Settings updated successfully');
      
      // Notify parent component of updated settings
      if (typeof onSettingsUpdated === 'function') {
        onSettingsUpdated({
          thresholdsSchedule,
          leakDailySchedule,
          signalStrengthSchedule,
          notificationDigestSchedule
        });
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      setError('Failed to update settings. Please try again later.');
      console.error('Error updating settings:', err);
    } finally {
      setSaving(false);
    }
  };
  
  // Handle cron changes
  const handleThresholdsChange = (e) => {
    const v = e.target.value;
    setThresholdsSchedule(v);
    setThresholdsDescription(parseCronDescription(v));
  };
  const handleLeakDailyChange = (e) => {
    const v = e.target.value;
    setLeakDailySchedule(v);
    setLeakDailyDescription(parseCronDescription(v));
  };
  const handleSignalStrengthChange = (e) => {
    const v = e.target.value;
    setSignalStrengthSchedule(v);
    setSignalStrengthDescription(parseCronDescription(v));
  };

  const handleNotificationDigestChange = (e) => {
    const v = e.target.value;
    setNotificationDigestSchedule(v);
    setNotificationDigestDescription(parseCronDescription(v));
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <LoadingSpinner />
      </Box>
    );
  }
  
  return (
    <Box>
      <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <CardHeader 
          title="Cron Settings" 
          sx={{ 
            backgroundColor: theme.palette.action.hover,
            py: 1.25
          }}
        />
        <CardContent sx={{ pt: 1.5 }}>
          {error && (
            <Alert 
              severity="error" 
              onClose={() => setError(null)} 
              sx={{ mb: 2 }}
            >
              <AlertTitle>Error</AlertTitle>
              {error}
            </Alert>
          )}
          
          {successMessage && (
            <Alert 
              severity="success" 
              onClose={() => setSuccessMessage(null)} 
              sx={{ mb: 2 }}
            >
              <AlertTitle>Success</AlertTitle>
              {successMessage}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="thresholdsSchedule"
                  label="Thresholds Schedule (Cron Expression)"
                  value={thresholdsSchedule}
                  onChange={handleThresholdsChange}
                  disabled={saving}
                  variant="outlined"
                  margin="dense"
                  size="small"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {thresholdsDescription}
                </Typography>
                <FormHelperText>
                  Use cron: minute hour day-of-month month day-of-week
                </FormHelperText>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="leakDailySchedule"
                  label="Leak Analysis Schedule (Cron Expression)"
                  value={leakDailySchedule}
                  onChange={handleLeakDailyChange}
                  disabled={saving}
                  variant="outlined"
                  margin="dense"
                  size="small"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {leakDailyDescription}
                </Typography>
                <FormHelperText>
                  Use cron: minute hour day-of-month month day-of-week
                </FormHelperText>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="signalStrengthSchedule"
                  label="Signal Strength Schedule (Cron Expression)"
                  value={signalStrengthSchedule}
                  onChange={handleSignalStrengthChange}
                  disabled={saving}
                  variant="outlined"
                  margin="dense"
                  size="small"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {signalStrengthDescription}
                </Typography>
                <FormHelperText>
                  Use cron: minute hour day-of-month month day-of-week
                </FormHelperText>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="notificationDigestSchedule"
                  label="Email Digest Schedule (Cron Expression)"
                  value={notificationDigestSchedule}
                  onChange={handleNotificationDigestChange}
                  disabled={saving}
                  variant="outlined"
                  margin="dense"
                  size="small"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {notificationDigestDescription}
                </Typography>
                <FormHelperText>
                  Use cron: minute hour day-of-month month day-of-week
                </FormHelperText>
              </Grid>
              
            </Grid>
          </Box>
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: 'space-between', p: 2, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="primary"
              type="button"
              disabled={saving || runningThresholds}
              onClick={handleRunThresholds}
              size="medium"
              startIcon={runningThresholds ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
            >
              {runningThresholds ? 'Running...' : 'Run Thresholds'}
            </Button>

            <Button
              variant="outlined"
              color="primary"
              type="button"
              disabled={saving || runningLeakDetection}
              onClick={handleRunLeakDetection}
              size="medium"
              startIcon={runningLeakDetection ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
            >
              {runningLeakDetection ? 'Running...' : 'Run Leak Detection'}
            </Button>

            <Button
              variant="outlined"
              color="primary"
              type="button"
              disabled={saving || runningSignalStrength}
              onClick={handleRunSignalStrength}
              size="medium"
              startIcon={runningSignalStrength ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
            >
              {runningSignalStrength ? 'Running...' : 'Run Signal Strength'}
            </Button>

            <Button
              variant="outlined"
              color="primary"
              type="button"
              disabled={saving || runningNotificationDigest}
              onClick={handleRunNotificationDigest}
              size="medium"
              startIcon={runningNotificationDigest ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
            >
              {runningNotificationDigest ? 'Running...' : 'Run Email Digest'}
            </Button>
          </Box>

          <Button 
            variant="contained" 
            color="primary" 
            type="submit"
            disabled={saving}
            onClick={handleSubmit}
            size="medium"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <FontAwesomeIcon icon={faSave} />}
          >
            {saving ? 'Saving...' : 'Save Cron Settings'}
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
};

export default NotificationSettings;
